import { useState } from 'react'
import { createPortal } from 'react-dom'
import { usePopoverPlacement } from './usePopoverPlacement'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  parse,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import { it } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, X } from 'lucide-react'

const WEEKDAYS = ['L', 'M', 'M', 'G', 'V', 'S', 'D']

function Calendar({ selected, onSelect }: { selected: Date | null; onSelect: (date: Date) => void }) {
  const [month, setMonth] = useState(selected && isValid(selected) ? selected : new Date())

  const monthStart = startOfMonth(month)
  const monthEnd = endOfMonth(month)
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 })
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd })
  const today = new Date()

  return (
    <div className="w-64 p-3">
      <div className="mb-2 flex items-center justify-between">
        <button type="button" onClick={() => setMonth((m) => subMonths(m, 1))} className="cursor-pointer rounded p-1 text-muted hover:bg-surface" aria-label="Mese precedente">
          <ChevronLeft size={16} />
        </button>
        <span className="text-sm font-medium text-heading capitalize">{format(month, 'MMMM yyyy', { locale: it })}</span>
        <button type="button" onClick={() => setMonth((m) => addMonths(m, 1))} className="cursor-pointer rounded p-1 text-muted hover:bg-surface" aria-label="Mese successivo">
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-y-1 text-center">
        {WEEKDAYS.map((d, i) => (
          <span key={i} className="text-xs font-medium text-muted">
            {d}
          </span>
        ))}
        {days.map((day) => {
          const inMonth = isSameMonth(day, month)
          const isSelected = selected && isValid(selected) && isSameDay(day, selected)
          const isToday = isSameDay(day, today)
          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onSelect(day)}
              className={`mx-auto flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-sm transition-colors ${
                isSelected
                  ? 'bg-primary-600 font-medium text-on-dark'
                  : isToday
                    ? 'font-medium text-link ring-1 ring-primary-300'
                    : inMonth
                      ? 'text-body hover:bg-surface-strong'
                      : 'text-faint hover:bg-surface'
              }`}
            >
              {format(day, 'd')}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function sanitizeDigits(value: string) {
  return value.replace(/\D/g, '').slice(0, 2)
}

function clampDigits(digits: string, max: number): string {
  if (digits === '') return ''
  const n = parseInt(digits, 10)
  if (Number.isNaN(n)) return ''
  return String(Math.min(n, max))
}

function pad2(v: string): string {
  if (v === '') return '00'
  return v.padStart(2, '0')
}

// I campi ora/minuti mantengono una bozza locale (draft) separata dal valore committato:
// il commit al padre avviene con valori sempre validi a 2 cifre (pad), ma il testo mostrato
// resta quello digitato, così si possono comporre numeri a 2 cifre (es. "30") senza che il
// pad intermedio del primo tasto ("3" -> "03") blocchi la digitazione del secondo.
function TimeFields({ hours, minutes, onChange }: { hours: string; minutes: string; onChange: (h: string, m: string) => void }) {
  const [hDraft, setHDraft] = useState<string | null>(null)
  const [mDraft, setMDraft] = useState<string | null>(null)
  const hShown = hDraft ?? hours
  const mShown = mDraft ?? minutes

  function handleHours(raw: string) {
    const clamped = clampDigits(sanitizeDigits(raw), 23)
    setHDraft(clamped)
    onChange(pad2(clamped), pad2(mShown))
  }

  function handleMinutes(raw: string) {
    const clamped = clampDigits(sanitizeDigits(raw), 59)
    setMDraft(clamped)
    onChange(pad2(hShown), pad2(clamped))
  }

  function blurHours() {
    const finalH = pad2(hShown)
    setHDraft(null)
    onChange(finalH, pad2(mShown))
  }

  function blurMinutes() {
    const finalM = pad2(mShown)
    setMDraft(null)
    onChange(pad2(hShown), finalM)
  }

  return (
    <div className="flex items-center gap-2 border-t border-border-subtle p-3">
      <Clock size={16} className="text-faint" />
      <input
        type="text"
        inputMode="numeric"
        value={hShown}
        onChange={(e) => handleHours(e.target.value)}
        onBlur={blurHours}
        className="w-10 rounded border border-border px-1.5 py-1 text-center text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
        placeholder="HH"
      />
      <span className="text-muted">:</span>
      <input
        type="text"
        inputMode="numeric"
        value={mShown}
        onChange={(e) => handleMinutes(e.target.value)}
        onBlur={blurMinutes}
        className="w-10 rounded border border-border px-1.5 py-1 text-center text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
        placeholder="MM"
      />
    </div>
  )
}

interface PickerTriggerProps {
  label?: string
  error?: string
  displayValue: string
  placeholder: string
  disabled?: boolean
  onClear?: () => void
  icon: React.ReactNode
}

function Trigger({ label, error, displayValue, placeholder, disabled, onClear, icon, onClick, id }: PickerTriggerProps & { onClick: () => void; id?: string }) {
  return (
    <div>
      {label && (
        <label htmlFor={id} className="mb-1 block text-sm font-medium text-body">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          id={id}
          type="button"
          disabled={disabled}
          onClick={onClick}
          className={`flex w-full cursor-pointer items-center rounded-md border bg-canvas px-3 py-2 text-left text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 disabled:cursor-not-allowed disabled:bg-surface disabled:text-faint sm:text-sm ${displayValue && onClear ? 'pr-8' : ''} ${error ? 'border-danger-500' : 'border-border-strong'}`}
        >
          <span className={`flex min-w-0 flex-1 items-center gap-2 whitespace-nowrap ${displayValue ? '' : 'text-muted'}`}>
            {icon}
            <span className="truncate">{displayValue || placeholder}</span>
          </span>
        </button>
        {displayValue && onClear ? (
          // Bottone separato (non annidato dentro il trigger): due elementi interattivi
          // innestati violano la regola di accessibilità "nested-interactive".
          <button
            type="button"
            aria-label="Cancella"
            onClick={(e) => {
              e.stopPropagation()
              onClear()
            }}
            className="absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer rounded p-0.5 text-muted hover:bg-surface-strong hover:text-muted"
          >
            <X size={14} />
          </button>
        ) : null}
      </div>
      {error && <p className="mt-1 text-xs text-negative">{error}</p>}
    </div>
  )
}

function usePopover() {
  return usePopoverPlacement({ fitContentWidth: true })
}

interface DateInputProps {
  label?: string
  error?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  id?: string
  disabled?: boolean
}

/** Sostituisce <input type="date">: stesso valore ISO "yyyy-MM-dd" in ingresso/uscita, calendario custom
 * al posto del picker nativo del browser (che varia per OS/locale e spesso mostra il formato USA mm/dd/yyyy). */
export function DateInput({ label, error, value, onChange, placeholder = 'Seleziona data...', id, disabled }: DateInputProps) {
  const { open, setOpen, anchorRef, popRef, panelStyle, portalTarget } = usePopover()
  const selected = value ? parseISO(value) : null
  const display = selected && isValid(selected) ? format(selected, 'dd/MM/yyyy') : ''

  return (
    <div ref={anchorRef} className="relative">
      <Trigger
        id={id}
        label={label}
        error={error}
        displayValue={display}
        placeholder={placeholder}
        disabled={disabled}
        onClear={() => onChange('')}
        icon={<CalendarIcon size={15} className="shrink-0 text-faint" />}
        onClick={() => setOpen((o) => !o)}
      />
      {open && portalTarget && createPortal(
        <div ref={popRef} style={panelStyle} className="pointer-events-auto fixed z-[var(--z-index-tooltip)] rounded-md border border-border bg-canvas shadow-lg">
          <Calendar
            selected={selected}
            onSelect={(day) => {
              onChange(format(day, 'yyyy-MM-dd'))
              setOpen(false)
            }}
          />
        </div>, portalTarget,
      )}
    </div>
  )
}

type DateTimeInputProps = DateInputProps

/** Sostituisce <input type="datetime-local">: valore ISO "yyyy-MM-dd'T'HH:mm". */
export function DateTimeInput({ label, error, value, onChange, placeholder = 'Seleziona data e ora...', id, disabled }: DateTimeInputProps) {
  const { open, setOpen, anchorRef, popRef, panelStyle, portalTarget } = usePopover()
  const selected = value ? parse(value, "yyyy-MM-dd'T'HH:mm", new Date()) : null
  const valid = selected && isValid(selected)
  const display = valid ? format(selected, 'dd/MM/yyyy HH:mm') : ''
  const hours = valid ? format(selected, 'HH') : '00'
  const minutes = valid ? format(selected, 'mm') : '00'

  function commit(day: Date, h: string, m: string) {
    const hh = (h || '00').padStart(2, '0')
    const mm = (m || '00').padStart(2, '0')
    onChange(`${format(day, 'yyyy-MM-dd')}T${hh}:${mm}`)
  }

  return (
    <div ref={anchorRef} className="relative">
      <Trigger
        id={id}
        label={label}
        error={error}
        displayValue={display}
        placeholder={placeholder}
        disabled={disabled}
        onClear={() => onChange('')}
        icon={<CalendarIcon size={15} className="shrink-0 text-faint" />}
        onClick={() => setOpen((o) => !o)}
      />
      {open && portalTarget && createPortal(
        <div ref={popRef} style={panelStyle} className="pointer-events-auto fixed z-[var(--z-index-tooltip)] rounded-md border border-border bg-canvas shadow-lg">
          <Calendar selected={valid ? selected : null} onSelect={(day) => commit(day, hours, minutes)} />
          <TimeFields hours={hours} minutes={minutes} onChange={(h, m) => commit(valid ? selected : new Date(), h, m)} />
        </div>, portalTarget,
      )}
    </div>
  )
}

interface TimeInputProps {
  label?: string
  error?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  id?: string
  disabled?: boolean
}

/** Sostituisce <input type="time">: valore "HH:mm". */
export function TimeInput({ label, error, value, onChange, placeholder = 'Seleziona ora...', id, disabled }: TimeInputProps) {
  const { open, setOpen, anchorRef, popRef, panelStyle, portalTarget } = usePopover()
  const [hours, minutes] = value ? value.split(':') : ['', '']

  return (
    <div ref={anchorRef} className="relative">
      <Trigger
        id={id}
        label={label}
        error={error}
        displayValue={value}
        placeholder={placeholder}
        disabled={disabled}
        onClear={() => onChange('')}
        icon={<Clock size={15} className="shrink-0 text-faint" />}
        onClick={() => setOpen((o) => !o)}
      />
      {open && portalTarget && createPortal(
        <div ref={popRef} style={panelStyle} className="pointer-events-auto fixed z-[var(--z-index-tooltip)] rounded-md border border-border bg-canvas shadow-lg">
          <TimeFields
            hours={hours || '00'}
            minutes={minutes || '00'}
            onChange={(h, m) => onChange(`${(h || '00').padStart(2, '0')}:${(m || '00').padStart(2, '0')}`)}
          />
        </div>, portalTarget,
      )}
    </div>
  )
}
